import { useMemo, useState } from 'react';
import styled from 'styled-components';

import DatePickerShortcuts from 'components/insights/filterBar/DatePickerShortcuts';
import FilterBar from 'components/shared/filters/FilterBar';
import FilterMultiSelect from 'components/shared/filters/FilterMultiSelect';
import DatePicker from 'components/shared/form/DatePicker';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useProfile } from 'context/profile';
import { capitalize } from 'lib/util';
import { ReviewStatus } from 'models/Review';
import { indexAdmissions } from 'services/api/admissions';
import { AdmissionsFilterState as Filters, useAdmissionsActions, useAdmissionsStore } from 'stores/admissionsStore';

import { getFilterSections } from './helpers/filters';
import usePrefetchGroups from './hooks/usePrefetchGroups';

interface AdmissionsFilterBarProps {
  onApplyFilters: (filters: { search: string; [key: string]: any }) => void;
}

function AdmissionsFilterBar({ onApplyFilters }: AdmissionsFilterBarProps) {
  const { profile } = useProfile();
  const filters = useAdmissionsStore((state) => state.filters);
  const dateRange = useAdmissionsStore((state) => state.dateRange);
  const { setFilters, setDateRange } = useAdmissionsActions();

  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(dateRange?.startDate ?? null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(dateRange?.endDate ?? null);
  const [filteredStartDate, setFilteredStartDate] = useState<Date | null>(dateRange?.startDate ?? null);
  const [filteredEndDate, setFilteredEndDate] = useState<Date | null>(dateRange?.endDate ?? null);
  const maxDate = useMemo(() => new Date(), []);

  const handleClearFilters = () => {
    setFilters({});
    setDateRange(null);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setFilteredStartDate(null);
    setFilteredEndDate(null);
  };

  const handleDateChange = (startDate: Date | null, endDate: Date | null) => {
    if (startDate && endDate) {
      setDateRange({ startDate, endDate });
    }
  };

  const DatePickerFooter = ({ dateInput }) => (
    <DatePickerShortcuts
      startDate={selectedStartDate}
      endDate={selectedEndDate}
      datePickerRef={dateInput}
      setDates={([startDate, endDate]) => {
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setFilteredStartDate(startDate);
        setFilteredEndDate(endDate);
        handleDateChange(startDate, endDate);
      }}
    />
  );

  const { groupType: snfGroupType, query: snfQuery } = usePrefetchGroups(SKILLED_NURSING_FACILITY);
  const hasMultipleGroups = (snfQuery.data?.meta.totalRecords ?? 0) > 1;
  const filterSections = useMemo(
    () => getFilterSections(snfGroupType, profile, { showGroupFilter: hasMultipleGroups }),
    [snfGroupType, hasMultipleGroups, profile]
  );

  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
  };

  return (
    <FilterBar
      applyFilters={(filters: Filters) => {
        onApplyFilters({ search: '', ...filters });
      }}
      filters={filters}
      filterSections={filterSections}
      onClearFilters={handleClearFilters}
      title='Admissions'
      resultsText='admissions'
      searchPlaceholder='Search'
      queryFn={(inp, signal) => indexAdmissions(inp, signal)}
      quickFilterShowMinWidth={1100}
      quickFilter={
        <FilterMultiSelect
          name='status'
          placeholder='Status'
          options={Object.values(ReviewStatus).map((status) => ({
            id: status,
            name: status === ReviewStatus.REJECTED ? 'Denied' : capitalize(status),
          }))}
          getOptionLabel={(obj) => obj?.name ?? ''}
          getOptionValue={(obj) => obj?.id ?? ''}
          value={filters.status || []}
          onChange={handleStatusChange}
        />
      }
      callToAction={
        <DatePickerContainer>
          <DatePicker
            data-gtm-id='admissionsStartDateFilter'
            onCalendarClose={() => {
              setFilteredStartDate(selectedStartDate);
              handleDateChange(selectedStartDate, filteredEndDate);
            }}
            onBlur={() => {
              setFilteredStartDate(selectedStartDate);
              handleDateChange(selectedStartDate, filteredEndDate);
            }}
            onChange={(val) => setSelectedStartDate(val as Date)}
            selected={selectedStartDate}
            maxDate={selectedEndDate ?? undefined}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />

          <Emdash>&mdash;</Emdash>

          <DatePicker
            data-gtm-id='admissionsEndDateFilter'
            onCalendarClose={() => {
              setFilteredEndDate(selectedEndDate);
              handleDateChange(filteredStartDate, selectedEndDate);
            }}
            onBlur={() => {
              setFilteredEndDate(selectedEndDate);
              handleDateChange(filteredStartDate, selectedEndDate);
            }}
            onChange={(val) => setSelectedEndDate(val as Date)}
            selected={selectedEndDate}
            maxDate={maxDate}
            minDate={selectedStartDate ?? undefined}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />
        </DatePickerContainer>
      }
    />
  );
}

export default AdmissionsFilterBar;

const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 160px;

    @media ${({ theme }) => theme.devices.desktop} {
      max-width: 140px;
    }
  }
`;

const Emdash = styled.span`
  margin: 0 8px;
`;
